import { computed, Injectable, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { map, take } from "rxjs";
import { FEATURE_FLAGS, TENANT_ROLE } from "../app.constants";
import { TenantUserList } from "../_objects/user";
import { FeatureFlagService } from "../_services/feature-flag/feature-flag.service";
import { LoginUserService } from "../_services/http-api/login-user.service";
import LiveAssetsStateService from "../_services/live-assets-state.service";
import { UsersStateService } from "../_services/user-state/users-state.service";
import { formatDate } from "../_utils/date.utils";

// Characters forbidden in Windows file/folder names: \ / : * ? " < > |
const WINDOWS_FORBIDDEN_CHARS = /[\\/:*?"<>|]/;

function noWindowsForbiddenChars(control: AbstractControl): ValidationErrors | null {
  const value: string = control.value ?? "";
  return WINDOWS_FORBIDDEN_CHARS.test(value) ? { windowsForbiddenChars: true } : null;
}

export type InspectionForm = {
  title: FormControl<string | null>;
  inspectors: FormControl<string[] | null>;
  liveAsset: FormControl<string | null>;
  inspectionDate: FormControl<Date | null>;
};

@Injectable()
export class InspectionFormService {
  public inspectors = toSignal(this.userStateService.users$.pipe(map(this.toInspectionOptions)), { initialValue: [] });
  public liveAssets = this.liveAssetState.liveAssets;
  public rootLiveAssets = this.liveAssetState.rootLiveAssets;
  public rootOnlyToggleEnabled = this.featureFlagService.isFlagEnabled(FEATURE_FLAGS.INSPECTION_ROOT_ONLY_ASSETS);
  public rootsOnly = signal(true);
  public liveAssetsOptions = computed(() => {
    const assets = this.rootsOnly() ? (this.rootLiveAssets() ?? []) : (this.liveAssets() ?? []);
    return assets
      .map((a) => ({ ...a, displayLabel: a.serialNo ? `${a.name}_${a.serialNo}` : a.name }))
      .sort((a, b) => a.displayLabel.localeCompare(b.displayLabel));
  });

  public form: FormGroup<InspectionForm> = this.fb.group({
    title: [
      this.getDefaultTitle({ inspectors: [this.userService.userId] }),
      [Validators.required, Validators.maxLength(100), noWindowsForbiddenChars]
    ],
    inspectors: [[this.userService.userId], Validators.required],
    liveAsset: ["", Validators.required],
    inspectionDate: [new Date(), Validators.required]
  });

  constructor(
    private fb: FormBuilder,
    private translateService: TranslateService,
    private liveAssetState: LiveAssetsStateService,
    private userService: LoginUserService,
    private userStateService: UsersStateService,
    private featureFlagService: FeatureFlagService
  ) {
    this.updateDefaultTitleOnChanges();
    this.liveAssetState.init().pipe(take(1)).subscribe();
  }

  private getDefaultTitle(
    changes: Partial<{
      title: string;
      inspectors: string[];
      liveAsset: string;
      inspectionDate: Date;
    }>
  ) {
    const firstInspectorId = changes.inspectors?.[0];

    let fullName = this.inspectors().find((i) => i.id === firstInspectorId)?.fullName ?? "";

    if (!fullName && firstInspectorId === this.userService.userId && this.userService.loginUser) {
      const { givenName = "", surname = "" } = this.userService.loginUser;
      fullName = `${givenName} ${surname}`.trim();
    }

    const foundAsset = this.liveAssetsOptions().find((i) => i.id === changes.liveAsset);
    const liveAsset = foundAsset?.displayLabel ?? this.translateService.instant("inspectionForm.liveAsset");
    return `${formatDate(changes.inspectionDate ?? new Date(), "yyyyMMdd")}-${fullName}-${liveAsset}`;
  }

  private toInspectionOptions(users: TenantUserList[]) {
    const allowedRoles = [TENANT_ROLE.ADMIN, TENANT_ROLE.USER, TENANT_ROLE.OWNER];

    return users
      .filter((i) => i.isActive && i.isSubscription && (!i.role || allowedRoles.includes(i.role)))
      .map((i) => ({
        fullName: `${i.givenName} ${i.surname}`,
        id: i.id
      }));
  }

  public preselectAsset(assetId: string): void {
    this.form.controls.liveAsset.setValue(assetId);
  }

  private updateDefaultTitleOnChanges() {
    const updateIfTitleNotdirty = () => {
      if (!this.form.controls.title.dirty) {
        this.form.controls.title.patchValue(this.getDefaultTitle(this.form.getRawValue()));
      }
    };
    this.form.controls.inspectionDate.valueChanges.subscribe(updateIfTitleNotdirty);
    this.form.controls.inspectors.valueChanges.subscribe(updateIfTitleNotdirty);
    this.form.controls.liveAsset.valueChanges.subscribe(updateIfTitleNotdirty);
  }
}
